import { LoadingSpinner } from '@approvalmax/ui';
import { Box } from '@approvalmax/ui/src/components';
import { compareHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { FC, memo, useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import { useGetWorkflowVersionDocumentFields } from 'shared/data/webApp/v1';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteCompanyCustomFields,
    useNetSuiteItemReceiptFieldSettings,
} from 'shared/hooks/(requests)';

import FieldsSection from '../../../../../components/FieldsSection';
import ActivitySection from '../../../../card/ActivitySection';
import AddCommentSection from '../../../../card/AddCommentSection/AddCommentSection';
import AttachmentsSection from '../../../../card/AttachmentsSection';
import Toolbar from '../../../../card/Toolbar/Toolbar';
import { WorkflowSection } from '../../../../card/WorkflowSection';
import { getNetSuiteAmountText, isFieldVisible } from '../../NetSuiteRequestCard.helpers';
import { useDisplayCurrency, useGetExchangeInfo } from '../../NetSuiteRequestCard.hooks';
import {
    CurrencySwitcherContainer,
    FieldsSectionWrap,
    Root,
    StyledField,
    StyledScrollableArea,
} from '../../NetSuiteRequestCard.styles';
import CurrencySwitcher from '../CurrencySwitcher/CurrencySwitcher';
import NetSuiteCustomFields from '../NetSuiteCustomFields/NetSuiteCustomFields';
import { NetSuiteItemReceiptExpenses } from '../NetSuiteItemReceiptExpenses/NetSuiteItemReceiptExpenses';
import { NetSuiteItemReceiptItemLines } from '../NetSuiteItemReceiptItemLines/NetSuiteItemReceiptItemLines';
import NetSuiteRequestFooter from '../NetSuiteRequestFooter/NetSuiteRequestFooter';
import { messages } from './RequestCardItemReceipt.messages';
import { RequestCardItemReceiptProps } from './RequestCardItemReceipt.types';

const qa = bemFactory.qa('netsuite-item-receipt-fields-section');

export const RequestCardItemReceipt: FC<RequestCardItemReceiptProps> = memo((props) => {
    const { request, className } = props;

    const { data: workflowVersionDocumentFields, isFetching: isFetchingGetWorkflowVersionDocumentFields } =
        useGetWorkflowVersionDocumentFields({
            query: {
                companyId: request.companyId,
                workflowVersionIds: [request.workflowVersionId],
            },
        });

    const { netSuiteCompanyCustomFields } = useNetSuiteCompanyCustomFields(request.companyId);

    const { details } = request;

    const fieldSettings = useNetSuiteItemReceiptFieldSettings(workflowVersionDocumentFields?.data);
    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(workflowVersionDocumentFields?.data);

    const expenseLines = useMemo(
        () =>
            [...details.expenseLines].sort(
                compareHelpers.comparatorFor<domain.NetSuiteItemReceiptExpenseLine>(
                    compareHelpers.numberComparator2Asc,
                    'lineNumber'
                )
            ),
        [details.expenseLines]
    );

    const itemLines = useMemo(
        () =>
            [...details.itemLines].sort(
                compareHelpers.comparatorFor<domain.NetSuiteItemReceiptItemLine>(
                    compareHelpers.numberComparator2Asc,
                    'lineNumber'
                )
            ),
        [details.itemLines]
    );

    const { displayCurrency, onDisplayCurrencyChange } = useDisplayCurrency();

    const { exchangeRate } = useGetExchangeInfo(request.companyId, details.exchangeRate, displayCurrency?.id);

    return (
        <Root className={className}>
            <Toolbar
                request={request}
                amountText={getNetSuiteAmountText(
                    request.amount,
                    exchangeRate,
                    request.currency || '',
                    displayCurrency?.isoCode
                )}
            />

            <StyledScrollableArea scrollResetKey={request.id}>
                {isFetchingGetWorkflowVersionDocumentFields ? (
                    <Box spacing='20'>
                        <LoadingSpinner />
                    </Box>
                ) : (
                    <>
                        <FieldsSectionWrap>
                            {isFieldVisible(fieldSettings.reference) && (
                                <StyledField
                                    qa={qa('reference')}
                                    title={messages.referenceFieldText}
                                    valueType={FieldsSection.Field.ValueType.String}
                                    value={details.reference}
                                    hideIfEmpty
                                />
                            )}

                            {isFieldVisible(fieldSettings.purchaseOrder) && (
                                <StyledField
                                    qa={qa('purchaseOrder')}
                                    title={messages.purchaseOrderFieldText}
                                    valueType={FieldsSection.Field.ValueType.String}
                                    value={details.purchaseOrder?.text}
                                    hideIfEmpty
                                />
                            )}

                            {isFieldVisible(fieldSettings.transactionDate) && (
                                <StyledField
                                    qa={qa('transactionDate')}
                                    title={messages.transactionDateFieldText}
                                    valueType={FieldsSection.Field.ValueType.Date}
                                    value={details.transactionDate}
                                />
                            )}

                            {isFieldVisible(fieldSettings.postingPeriod) && (
                                <StyledField
                                    qa={qa('postingPeriod')}
                                    title={messages.postingPeriodFieldText}
                                    valueType={FieldsSection.Field.ValueType.String}
                                    value={details.postingPeriod?.text}
                                    hideIfEmpty
                                />
                            )}

                            {isFieldVisible(fieldSettings.memo) && (
                                <StyledField
                                    qa={qa('memo')}
                                    title={messages.memoFieldText}
                                    valueType={FieldsSection.Field.ValueType.String}
                                    value={details.memo}
                                    hideIfEmpty
                                />
                            )}

                            <NetSuiteCustomFields
                                customFields={details.customFields}
                                companyCustomFields={netSuiteCompanyCustomFields}
                            />
                        </FieldsSectionWrap>

                        <NetSuiteItemReceiptExpenses
                            expenseLines={expenseLines}
                            companyCustomFields={netSuiteCompanyCustomFields}
                            documentFields={workflowVersionDocumentFields?.data}
                            exchangeRate={
                                displayCurrency && displayCurrency?.isoCode !== request.currency && exchangeRate !== 1
                                    ? exchangeRate
                                    : null
                            }
                        />

                        <NetSuiteItemReceiptItemLines
                            itemLines={itemLines}
                            companyCustomFields={netSuiteCompanyCustomFields}
                            documentFields={workflowVersionDocumentFields?.data}
                            exchangeRate={
                                displayCurrency && displayCurrency?.isoCode !== request.currency && exchangeRate !== 1
                                    ? exchangeRate
                                    : null
                            }
                        />

                        {typeof exchangeRate === 'number' && (
                            <CurrencySwitcherContainer>
                                <CurrencySwitcher
                                    request={request}
                                    selectedCurrency={displayCurrency}
                                    requestCurrency={details.currency}
                                    onCurrencyChange={onDisplayCurrencyChange}
                                    companyId={request.companyId}
                                />
                            </CurrencySwitcherContainer>
                        )}
                    </>
                )}

                <NetSuiteRequestFooter
                    request={request}
                    displayCurrency={displayCurrency}
                    exchangeRate={exchangeRate}
                />

                {isFieldVisible(attachmentsFieldSettings.attachments) && <AttachmentsSection request={request} />}

                <WorkflowSection request={request} />

                <ActivitySection request={request} />

                {!request.flags.status.isDraft && <AddCommentSection request={request} />}
            </StyledScrollableArea>
        </Root>
    );
});

RequestCardItemReceipt.displayName = 'RequestCardItemReceipt';
